import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Globe,
    Key,
    Eye,
    EyeOff,
    Copy,
    Check,
    CheckCircle2,
    Building,
    Mail,
    MapPin,
    Languages,
    Calendar
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import type { Tenant } from '@/api/tenants/tenants.types';
import { Badge } from '@/components/ui/badge';

interface TenantSummaryCardProps {
    tenant: Tenant;
}

export const TenantSummaryCard = ({ tenant }: TenantSummaryCardProps) => {
    const [showToken, setShowToken] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const copyToClipboard = async (text: string, fieldId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldId);
            setTimeout(() => setCopiedField(null), 2000);
        } catch {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            setCopiedField(fieldId);
            setTimeout(() => setCopiedField(null), 2000);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getLanguageDisplayName = (langCode: string) => {
        const languageMap: Record<string, string> = {
            'en': 'English',
            'de': 'German'
        };
        return languageMap[langCode] || langCode.toUpperCase();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            {/* Organization Details Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Building className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Organization Details</CardTitle>
                            <CardDescription>
                                Basic information about your messaging organization
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Organization Name */}
                        <div className="space-y-2 md:col-span-2">
                            <Label className="flex items-center gap-2">
                                <Building className="h-4 w-4 text-primary" />
                                Organization Name
                            </Label>
                            <Input
                                type="text"
                                value={tenant.name}
                                readOnly
                                className="h-9 bg-muted/50"
                            />
                        </div>

                        {/* Status & Language */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Status & Language</Label>
                            <div className="flex items-center gap-2 h-9">
                                <Badge variant="secondary" className="px-2.5 py-1 text-xs flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                                    {tenant.status}
                                </Badge>
                                <Badge variant="outline" className="px-2.5 py-1 text-xs flex items-center gap-1">
                                    <Languages className="h-3 w-3 text-blue-500" />
                                    {getLanguageDisplayName(tenant.language)}
                                </Badge>
                            </div>
                        </div>

                        {/* Tenant ID */}
                        <div className="space-y-2 md:col-span-2">
                            <Label className="flex items-center gap-2">
                                <Key className="h-4 w-4 text-primary" />
                                Tenant ID
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="text"
                                    value={tenant.id}
                                    readOnly
                                    className="h-9 flex-1 font-mono text-sm bg-muted/50"
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 shrink-0"
                                    onClick={() => copyToClipboard(tenant.id, 'tenant_id')}
                                >
                                    {copiedField === 'tenant_id' ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Created Date */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-primary" />
                                Created Date
                            </Label>
                            <Input
                                type="text"
                                value={formatDate(tenant.metadata.created_at)}
                                readOnly
                                className="h-9 bg-muted/50"
                            />
                        </div>

                        {/* Contact Email */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-primary" />
                                Contact Email
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input 
                                    type="text"
                                    value={tenant.email}
                                    readOnly 
                                    className="h-9 flex-1 text-sm bg-muted/50" 
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 shrink-0"
                                    onClick={() => copyToClipboard(tenant.email, 'email')}
                                >
                                    {copiedField === 'email' ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Business Address */}
                        <div className="space-y-2 md:col-span-2">
                            <Label className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-primary" />
                                Business Address
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input 
                                    type="text"
                                    value={tenant.full_address}
                                    readOnly 
                                    className="h-9 flex-1 text-sm bg-muted/50" 
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 shrink-0"
                                    onClick={() => copyToClipboard(tenant.full_address, 'address')}
                                >
                                    {copiedField === 'address' ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* API Integration Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Globe className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>API Integration</CardTitle>
                            <CardDescription>
                                Connect your services using these credentials
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* API Endpoint */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-primary" />
                                API Endpoint
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input 
                                    type="text"
                                    value={tenant.host}
                                    readOnly 
                                    className="h-9 flex-1 font-mono text-sm bg-muted/50" 
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 shrink-0"
                                    onClick={() => copyToClipboard(tenant.host, 'api_endpoint')}
                                >
                                    {copiedField === 'api_endpoint' ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Authentication Token */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Key className="h-4 w-4 text-primary" />
                                Auth Token
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input 
                                    type="text"
                                    value={showToken 
                                        ? tenant.third_party_token 
                                        : tenant.third_party_token.length > 16 
                                            ? `${tenant.third_party_token.substring(0, 8)}****${tenant.third_party_token.substring(tenant.third_party_token.length - 8)}`
                                            : '•'.repeat(tenant.third_party_token.length)
                                    }
                                    readOnly 
                                    className="h-9 flex-1 font-mono text-sm bg-muted/50" 
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowToken(!showToken)}
                                    className="h-9 w-9 shrink-0"
                                >
                                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 shrink-0"
                                    onClick={() => copyToClipboard(tenant.third_party_token, 'auth_token')}
                                >
                                    {copiedField === 'auth_token' ? (
                                        <Check className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};
